import { HttpErrorCode } from '@copera/common/error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, isPast } from 'date-fns';
import { useMemo } from 'react';
import { BillingStatus } from 'src/@types/billing';
import { BillingRequests } from 'src/requests/billing';
import { QueryKey, queryRetry } from 'src/utils/react-query';

export function useSubscription() {
  const queryClient = useQueryClient();

  const {
    data: subscription,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    [QueryKey.SUBSCRIPTION],
    () => BillingRequests.getSubscription(),
    {
      keepPreviousData: true,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 60 * 4,
      retry: queryRetry,
    },
  );

  const { mutateAsync: firstUpgradeSubscription } = useMutation({
    mutationFn: BillingRequests.firstUpgradeSubscription,
    onSuccess: (updatedSubscription) => {
      queryClient.setQueryData<typeof subscription>(
        [QueryKey.SUBSCRIPTION],
        updatedSubscription,
      );
    },
    meta: {
      httpErrors: {
        [HttpErrorCode.FORBIDDEN]: 'error.billing.cannot_upgrade_free_plan',
        [HttpErrorCode.INVALID]: 'error.billing.subscription_no_items',
      },
    },
  });

  const { mutateAsync: addCard } = useMutation({
    mutationFn: BillingRequests.addCard,
    onSuccess: (updatedSubscription) => {
      queryClient.setQueryData<typeof subscription>(
        [QueryKey.SUBSCRIPTION],
        updatedSubscription,
      );
    },
    meta: {
      httpErrors: {
        [HttpErrorCode.MISSING_PARAMS]: 'error.billing.card_already_registered',
        [HttpErrorCode.UNKNOWN]: 'error.billing.card_add_failed',
      },
    },
  });

  const { mutateAsync: changeDefaultCard } = useMutation({
    mutationFn: BillingRequests.changeDefaultCard,
    onSuccess: (updatedSubscription) => {
      queryClient.setQueryData<typeof subscription>(
        [QueryKey.SUBSCRIPTION],
        updatedSubscription,
      );
    },
    meta: {
      httpErrors: {
        [HttpErrorCode.NOT_FOUND]: 'error.billing.card_not_found',
      },
    },
  });

  const { mutateAsync: removeCard } = useMutation({
    mutationFn: BillingRequests.removeCard,
    onSuccess: (updatedSubscription) => {
      queryClient.setQueryData<typeof subscription>(
        [QueryKey.SUBSCRIPTION],
        updatedSubscription,
      );
    },
    meta: {
      httpErrors: {
        [HttpErrorCode.NOT_FOUND]: 'error.billing.card_not_found',
        [HttpErrorCode.UNPROCESSABLE_ENTITY]:
          'error.billing.cannot_remove_default_card',
      },
    },
  });

  const freePlanMembersExceeded = useMemo(() => {
    const users =
      subscription?.members?.filter((m: any) => m?.type === 'user') || [];
    return subscription?.plan.free && users?.length > 2;
  }, [subscription]);

  const expired = useMemo(() => {
    const expiredAt = addDays(new Date(subscription?.createdAt || ''), 7);
    return isPast(expiredAt);
  }, [subscription]);

  const workspaceLocked = useMemo(
    () =>
      subscription?.status === BillingStatus.PAUSED ||
      (freePlanMembersExceeded && expired),
    [subscription, freePlanMembersExceeded, expired],
  );

  const memoizedValue = useMemo(
    () => ({
      subscription,
      isLoading,
      isFetching,
      refetch,

      firstUpgradeSubscription,
      addCard,
      changeDefaultCard,
      removeCard,
      workspaceLocked,
      freePlanMembersExceeded,
    }),
    [
      subscription,
      isLoading,
      isFetching,
      refetch,
      firstUpgradeSubscription,
      addCard,
      changeDefaultCard,
      removeCard,
      workspaceLocked,
      freePlanMembersExceeded,
    ],
  );

  return memoizedValue;
}
